import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { SmartField } from '../components/Inputs'
import RoofPitch3D from '../components/RoofPitch3D'
import { computeRoofPitch, DEFAULT_ROOF_PITCH, ftToFtInStr } from '../calculators/roofPitch'
import type { RoofPitchInputs } from '../calculators/roofPitch'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/roof-pitch/'
const CATEGORY = 'roofing-and-insulation'

const TITLE = 'Roof Pitch Calculator — Rafter Length, Rise & Roof Angle | RenoSheets'
const DESC = 'Free roof pitch calculator. Enter span and pitch to get rafter length (with overhang), rise, run, roof angle and slope factor. SVG cross-section diagram. No signup.'

const FAQS: QA[] = [
  { q: 'What does a 6/12 roof pitch mean?', a: 'A 6/12 roof pitch means the roof rises 6 inches vertically for every 12 inches of horizontal run. It is the most common residential pitch in North America — steep enough to shed water and snow well, but shallow enough to walk on safely during installation. The number before the slash is the rise; 12 is always the run.' },
  { q: 'How do I calculate rafter length from pitch and span?', a: 'Split the span in half to get the run, multiply (pitch ÷ 12) by the run to get the rise, then take the square root of run² + rise². That gives you the body length of the rafter from ridge to top plate. For the total cut length, add the overhang multiplied by the slope factor: √(pitch² + 144) ÷ 12.' },
  { q: 'What is the slope factor?', a: 'The slope factor is how much longer a sloped surface is than its horizontal projection. For a 6/12 pitch the slope factor is about 1.118 — meaning every 1 ft of horizontal distance equals 1.118 ft along the rafter. Use it to convert horizontal eave overhang into the extra rafter length you need to cut.' },
  { q: 'What is the steepest pitch I can walk on?', a: 'Most roofers consider anything up to 6/12 walkable in dry conditions without specialized gear. Between 7/12 and 9/12 you should be wearing rubber-soled boots, using a safety harness, and ideally roof jacks for footing. Anything 10/12 or steeper requires staging, scaffolding, or a roof ladder — it is no longer reasonable to free-climb.' },
  { q: 'How do I measure the pitch of an existing roof?', a: 'Set a 12-inch level horizontally against the underside of a rafter so one end touches the rafter and the other end sticks out into the attic. Measure straight down from the 12-inch mark to the rafter. That distance in inches is the rise — so a 5-inch measurement means a 5/12 pitch. You can also use a smartphone protractor app held flat against the roof surface.' },
  { q: 'What pitch counts as a "low slope" roof?', a: 'Anything below 4/12 is generally considered low slope. Asphalt shingles can technically be installed down to 2/12 with extra underlayment, but below 3/12 most building codes require a fully adhered membrane (modified bitumen, EPDM, or TPO) instead of shingles to prevent wind-driven rain from backing under the joints.' },
]

export default function RoofPitchCalculator() {
  const locale = useLocale()
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<RoofPitchInputs>(DEFAULT_ROOF_PITCH)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeRoofPitch(i, locale), [i, locale])
  const set = (patch: Partial<RoofPitchInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }

  const onSave = async () => {
    await saveSheet({
      id: genId(),
      type: 'roof-pitch',
      name: `Roof Pitch — ${i.spanFt} ft span @ ${i.pitch}/12`,
      createdAt: new Date().toISOString(),
      summary: `${r.rafterTotalFt.toFixed(2)} ft rafter · ${r.angleDeg.toFixed(1)}°`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'roof-pitch')
  const assumptions = `${i.spanFt} ft span at ${i.pitch}/12 pitch · ${i.overhangIn} in eave overhang.`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeRoofPitch: run = span/2, rise = pitch/12 × run, rafter body
  // = √(run² + rise²), slope factor = √(pitch² + 144)/12, total = body + overhang × slope factor.
  const overhangFt = i.overhangIn / 12
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {locale === 'fr'
          ? `Course = portée ÷ 2 = ${i.spanFt} ÷ 2`
          : `Run = span ÷ 2 = ${i.spanFt} ÷ 2`}
        {' '}= <strong>{r.runFt} {locale === 'fr' ? 'pi' : 'ft'}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Montée = (${i.pitch}/12) × ${r.runFt}`
          : `Rise = (${i.pitch}/12) × ${r.runFt}`}
        {' '}= <strong>{r.riseFt} {locale === 'fr' ? 'pi' : 'ft'}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Corps du chevron = √(${r.runFt}² + ${r.riseFt}²)`
          : `Rafter body = √(${r.runFt}² + ${r.riseFt}²)`}
        {' '}= <strong>{r.rafterBodyFt} {locale === 'fr' ? 'pi' : 'ft'}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Facteur de pente = √(${i.pitch}² + 144) ÷ 12`
          : `Slope factor = √(${i.pitch}² + 144) ÷ 12`}
        {' '}= <strong>{r.slopeFactor}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `+ Débord ${i.overhangIn} po (${overhangFt.toFixed(2)} pi) × ${r.slopeFactor}`
          : `+ Overhang ${i.overhangIn} in (${overhangFt.toFixed(2)} ft) × ${r.slopeFactor}`}
        {' '}= <strong>{r.rafterTotalFt} {locale === 'fr' ? 'pi' : 'ft'}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Angle = arctan(${i.pitch} ÷ 12)`
          : `Angle = arctan(${i.pitch} ÷ 12)`}
        {' '}= <strong>{r.angleDeg}°</strong>
      </li>
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={TITLE} description={DESC} path={PATH} schema={softwareAppSchema(calcName('roof-pitch', 'Roof Pitch Calculator', locale), DESC, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Roofing & Insulation', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('roof-pitch', 'Roof Pitch Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">Roof Pitch Calculator</h1>
      <p className="mt-1 max-w-content text-text-muted">Enter the building span and roof pitch to get rafter length, rise, roof angle and slope factor — with a scaled cross-section diagram.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          {/* Number entry + slider on each field — type a precise value or drag,
              and the 3D house updates live. */}
          <div className="rounded-md border border-border p-3">
            <p className="mb-1 text-sm font-medium text-text-body">
              Building span
              <InfoTip text="Full horizontal distance across the building, eave to eave. The run is half of this." />
            </p>
            <SmartField label="Span" imperialValue={i.spanFt} onChangeImperial={(n) => set({ spanFt: n })} imperialUnit="ft" step={0.5} />
            <input type="range" min={4} max={60} step={0.5} value={i.spanFt} onChange={(e) => set({ spanFt: parseFloat(e.target.value) })} className="mt-2 w-full accent-brand" aria-label="Building span slider" />
          </div>

          <div className="mt-4">
            <span className="mb-1 flex items-center justify-between text-sm font-medium text-text-body">
              <span className="flex items-center">
                Roof pitch (X/12)
                <InfoTip text="Rise per 12 in of run. 6/12 is the most common residential pitch. Lower = shallower, higher = steeper." />
              </span>
              <span className="tnum text-brand-text">{i.pitch}/12</span>
            </span>
            <input type="range" min={1} max={24} step={1} value={i.pitch} onChange={(e) => set({ pitch: parseInt(e.target.value) })} className="w-full accent-brand" aria-label="Roof pitch slider" />
            <p className="mt-1 text-xs text-text-subtle">
              Slope factor: {r.slopeFactor} · {r.pitchPercent}% grade · {r.angleDeg}° angle
            </p>
          </div>

          <div className="mt-4 rounded-md border border-border p-3">
            <p className="mb-1 text-sm font-medium text-text-body">
              Eave overhang
              <InfoTip text="Horizontal distance the rafter extends past the wall. 12–24 in is typical for residential — the calculator multiplies this by the slope factor to add to the rafter length." />
            </p>
            <SmartField label="Overhang" imperialValue={i.overhangIn} onChangeImperial={(n) => set({ overhangIn: n })} imperialUnit="in" step={1} />
            <input type="range" min={0} max={48} step={1} value={i.overhangIn} onChange={(e) => set({ overhangIn: parseFloat(e.target.value) })} className="mt-2 w-full accent-brand" aria-label="Eave overhang slider" />
          </div>

          <div className="mt-5 flex flex-col items-center rounded-lg bg-surface-2 p-3">
            <p className="mb-1 self-center text-sm font-semibold text-text">{i.pitch}/12 pitch</p>
            <RoofPitch3D pitch={i.pitch} run={r.runFt} locale={locale} />
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: 'Rafter length (per side)', label: ftToFtInStr(r.rafterTotalFt), qty: r.rafterTotalFt, unit: 'ft', query: 'framing nails 16d galvanized' }}
            secondary={{ name: 'Rise', label: ftToFtInStr(r.riseFt), qty: r.riseFt, unit: 'ft', query: 'speed square swanson rafter' }}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{locale === 'fr' ? 'Ce qui détermine vraiment la pente d’un toit' : 'What actually determines a roof\'s pitch'}</h2>
        <p className="mt-3">
          {locale === 'fr'
            ? 'La pente du toit n’est pas qu’un choix esthétique — elle dépend du climat, du matériau de toiture et du type de structure. Les pentes plus abruptes (8-en-12 et plus) évacuent la neige et la pluie plus vite, c’est pourquoi elles sont standards dans les régions à fortes chutes de neige, tandis que les toits à faible pente sont plus courants dans les climats secs ou doux. Mais le matériau de toiture a son propre minimum : les bardeaux d’asphalte ont généralement besoin d’au moins une pente de 2-en-12 à 3-en-12 pour bien évacuer l’eau, tandis que tout ce qui est plus plat a besoin d’un système de toiture à membrane. La pente détermine aussi si un toit utilise des chevrons ou des fermes — les toitures abruptes et personnalisées sont habituellement construites sur place avec des chevrons, tandis que les pentes simples et constantes sont souvent conçues comme des fermes préfabriquées, plus rapides à installer mais plus difficiles à modifier plus tard. La longueur du chevron n’est pas que la montée et la course non plus : la coupe en bec d’oiseau à la sablière et le débord au-delà ajoutent tous deux à la longueur totale de planche à acheter, et se tromper sur l’un ou l’autre pour une pente abrupte peut vouloir dire une commande supplémentaire coûteuse.'
            : 'Roof pitch isn\'t just a design choice — it\'s driven by climate, roofing material, and structural type. Steeper pitches (8-in-12 and above) shed snow and rain faster, which is why they\'re standard in heavy-snowfall regions, while low-slope roofs are more common in dry or mild climates. But the roofing material has its own minimum: asphalt shingles typically need at least a 2-in-12 to 3-in-12 pitch to shed water properly, while anything flatter needs a membrane roofing system instead. Pitch also determines whether a roof uses rafters or trusses — steep custom rooflines are usually rafter-built on site, while simple, consistent pitches are often engineered as prefabricated trusses, which is faster to install but harder to modify later. Rafter length isn\'t just rise and run either: the birdsmouth cut at the wall plate and the overhang beyond it both add to the total board length you need to buy, and getting either wrong on a steep pitch can mean an expensive reorder.'}
        </p>
        <h2 className="mt-8 text-2xl">How to calculate roof pitch</h2>
        <p className="mt-3">Roof pitch is the ratio of vertical rise to horizontal run, expressed as X-in-12. A 6/12 pitch means 6 inches of vertical rise for every 12 inches of horizontal travel. Once you know the pitch and the building span, every other measurement falls out of basic right-triangle geometry:</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{`run    = span ÷ 2
rise   = (pitch ÷ 12) × run
rafter = √(run² + rise²)            ← body length, no overhang
slope factor = √(pitch² + 144) ÷ 12
total rafter = rafter + (overhang × slope factor)
angle  = arctan(pitch ÷ 12)`}</pre>
        <p className="mt-3">The slope factor is the same value you multiply a roof footprint by to get the actual sloped roof area — it shows up in every roofing material calculation, not just rafters.</p>

        <h3 className="mt-6 text-xl">Common pitches and what they look like</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><strong>2/12 to 3/12</strong> — Low slope. Shingles need extra underlayment; below 3/12 most codes require membrane roofing.</li>
          <li><strong>4/12 to 5/12</strong> — Standard low residential. Easy to walk, sheds water adequately.</li>
          <li><strong>6/12</strong> — The North American residential default. Visible but not steep.</li>
          <li><strong>7/12 to 9/12</strong> — Steeper styles like Cape Cod and Colonial. Safety harness recommended.</li>
          <li><strong>10/12 and up</strong> — Steep — roof jacks or staging required to walk safely.</li>
          <li><strong>12/12</strong> — Exactly 45°. Equal rise and run.</li>
        </ul>

        <h3 className="mt-6 text-xl">Common mistakes</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><strong>Forgetting overhang.</strong> The rafter has to extend past the wall — typically 12 to 24 inches. Cut to body length only and the eave will be flush with the siding.</li>
          <li><strong>Using span instead of run.</strong> The pitch formula uses the run (half the span). Plugging in the full span doubles the rise.</li>
          <li><strong>Measuring pitch from the underside.</strong> A 12 in level held against the underside of a rafter gives the right number — but only if the rafter is straight. Old sagging rafters lie.</li>
          <li><strong>Mixing units.</strong> Always work in inches for the X/12 ratio, then convert the run and rise to whichever unit you want for the final cut.</li>
        </ul>
      </article>

      <Faq items={FAQS} />

      <section className="mt-10">
        <h2 className="text-2xl">Related calculators</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">
        Reviewed by the RenoSheets team for calculation accuracy. Method: right-triangle geometry with the standard slope factor √(pitch² + 144) ÷ 12 for the overhang projection. Last updated 2026-06-02. Estimates only — verify rafter dimensions against your framing plan before cutting.
      </div>
    </div>
  )
}
